import { useEffect, useReducer, useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import cn from "classnames";
import { CountriesSelectList } from "components/ui/CountriesSelectList";
import { ErrorMessage } from "components/ui/ErrorMessage";
import { Input } from "components/ui/Input";
import { Loader } from "components/ui/Loader/Loader";
import { COUNTRIES_LIST } from "constants/countries";
import { VALIDATION_SCHEMA } from "constants/validationSchema";
import { useClickOutside } from "hooks/useClickOutside";
import { useKeyPress } from "hooks/usekeyPress";
import { ArrowDownIcon } from "../Icons";
import { initialState, keysReducer } from "./keysReducer";
import style from "./CountriesSelect.module.scss";

type Country = {
  name: string;
  iso: string;
  countryCode: string;
};

type CountriesSelectProps = {
  userCountry: Country;
  countries: string[];
  isCountryLoading: boolean;
  className?: string;
};

export const CountriesSelect = ({ userCountry, countries, isCountryLoading, className }: CountriesSelectProps) => {
  const [state, dispatch] = useReducer(keysReducer, initialState);
  const [openList, setOpenList] = useState<boolean>(false);
  const [searchedCountry, setSearchedCountry] = useState<string>("");
  const [filteredCountries, setFilteredCountries] = useState<string[] | null>(null);
  const selectCountriesRef = useRef<HTMLDivElement>(null);
  const currentItemRef = useRef<HTMLLIElement>(null);
  const arrowUpPressed = useKeyPress(openList, "ArrowUp");
  const arrowDownPressed = useKeyPress(openList, "ArrowDown");
  const enterPressed = useKeyPress(openList, "Enter");
  const { setValue, control } = useFormContext();

  useClickOutside(selectCountriesRef, () => setOpenList(false));

  useEffect(() => {
    setValue("countryCode", userCountry.countryCode);
    setSearchedCountry(userCountry.name);
  }, [userCountry.countryCode, userCountry.name]);

  useEffect(() => onSelectCountry(), [enterPressed]);

  useEffect(() => {
    if (currentItemRef.current) {
      currentItemRef.current.focus();
    }

    const arrowPressAction = (type: string) => {
      dispatch({ type, payload: { list: filteredCountries || countries } });
    };

    if (arrowUpPressed) {
      arrowPressAction("arrowUp");
    }

    if (arrowDownPressed) {
      arrowPressAction("arrowDown");
    }
  }, [arrowUpPressed, arrowDownPressed]);

  const resetSelectedIndex = () => {
    dispatch({ type: "select", payload: { index: 0 } });
  };

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setOpenList(true);
    setSearchedCountry(value);
    setFilteredCountries(countries.filter((country) => country.toLowerCase().startsWith(value.toLowerCase())));
  };

  const onBlur = () => {
    if (!countries.includes(searchedCountry) && !openList) {
      setSearchedCountry("");
      setValue("countryCode", "");
    }

    resetSelectedIndex();
  };

  const onSelectCountry = (e?: React.MouseEvent<HTMLLIElement>) => {
    if (currentItemRef.current) {
      const nameCountry = e?.currentTarget.textContent || currentItemRef.current.innerText;
      const country = COUNTRIES_LIST.find((country) => country.name === nameCountry);
      setValue("countryCode", country?.countryCode);
      setSearchedCountry(nameCountry);
      setOpenList(false);
    }
  };

  const onClick = () => {
    if (isCountryLoading) return;

    setFilteredCountries(null);
    setOpenList(!openList);
  };

  return (
    <div className={style.countriesSelect} ref={selectCountriesRef}>
      <Controller
        name="countryCode"
        control={control}
        rules={VALIDATION_SCHEMA.country}
        render={({ fieldState: { error } }) => (
          <>
            <div className={style.countriesLabelWrapper}>
              <label className={style.countriesSelectLabel}>Country</label>
            </div>
            {isCountryLoading ? (
              <div className={style.countriesSelectLoader}>
                <Loader />
              </div>
            ) : (
              <div className={style.countriesSelectInputWrapper}>
                <Input
                  type="countryCode"
                  name="user-country"
                  placeholder={!isCountryLoading ? "Choose country" : ""}
                  onClick={onClick}
                  onBlur={onBlur}
                  onChange={onChangeHandler}
                  value={searchedCountry || ""}
                  error={error}
                  className={className}
                  autoComplete="off"
                />
                {openList && (
                  <CountriesSelectList
                    countries={countries}
                    filteredCountries={filteredCountries}
                    state={state}
                    onSelectCountry={onSelectCountry}
                    currentItemRef={currentItemRef}
                  />
                )}
                <ArrowDownIcon className={cn(style.openListIcon, { [style.openListIconActive]: openList })} />
                <ErrorMessage className={style.errorMessage} error={error} />
              </div>
            )}
          </>
        )}
      />
    </div>
  );
};
